import { csrfHeaders } from '@hyperio/core/csrf.js';

/**
 * Prompts d'étude du thème astral.
 *
 * Chaque bouton [data-study-render] POST le JSON du thème (celui de la roue .sky-chart) au rendu de
 * template (stateless, rien n'est persisté) et affiche le prompt rendu dans une zone copiable.
 * L'URL du bouton vient de <smart:url>, donc préfixée par le tier de la page courante — le rendu a
 * pour plancher le tier compte, comme l'outil qui l'appelle. La chaîne est protégée CSRF → on joint
 * le token (meta _csrf / _csrf_header).
 */
import { localizeAstroJson } from '../astro-i18n/astro-i18n.js';


/**
 * Ecrit le rendu dans son conteneur, quel qu'il soit.
 *
 * Les invites d'etude vont dans un <textarea> — on les modifie parfois avant de les coller. Le
 * document du theme, lui, va dans un <pre><code> : c'est du markdown, il se lit mieux colore, et on
 * ne l'edite pas.
 *
 * La coloration est chargee A LA DEMANDE et ne s'amorce QU'AU CHARGEMENT DE LA PAGE (elle cherche
 * les `pre code` presents, cf. l'index du theme). Un bloc insere apres coup ne serait donc jamais
 * colore : on rappelle le module, dont l'import est mis en cache par le navigateur.
 */
/** Ce que le conteneur porte — `.value` pour un champ, le texte pour un bloc de code. */
function read(target) {
    return target.tagName === 'TEXTAREA' ? target.value : (target.querySelector('code') || target).textContent;
}

function write(target, text) {
    if (target.tagName === 'TEXTAREA') {
        target.value = text;
        return;
    }
    const code = target.querySelector('code') || target;
    code.textContent = text;
    // On colore CE bloc, explicitement. Ré-importer le module ne suffit pas : son
    // `highlightAll()` ne s'exécute qu'à la première évaluation, et le bloc vide présent au
    // chargement l'a déjà déclenchée — le document arrivait donc toujours en noir et blanc.
    import('@hyperio/components/code-highlight/code-highlight.js')
        .then((module) => module.highlightElement(code))
        .catch(() => {});
}

function attach(root) {
    const chartScript = document.querySelector('.sky-chart script[type="application/json"]');
    if (!chartScript) {
        return; // pas de thème calculé
    }
    // Le thème est calculé avec des enums anglais stables (neutres pour la roue). On les localise
    // dans la langue de la page AVANT de POSTer au template Mustache, pour un prompt directement
    // dans la langue du visiteur (langue inconnue ⇒ JSON inchangé, l'anglais reste lisible).
    const lang = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
    const chartJson = localizeAstroJson(chartScript.textContent, lang);

    const output = root.querySelector('.ui-study-output');
    const titleEl = root.querySelector('.ui-study-output-title');
    const promptEl = root.querySelector('.ui-study-prompt');

    root.querySelectorAll('[data-study-render]').forEach((button) => {
        button.addEventListener('click', () => {
            const url = button.dataset.studyRender;
            // Une source EXPLICITE l'emporte sur le JSON de la roue : le document Markdown a besoin
            // du theme deja projete (degres en minutes, maisons, libelles), pas des longitudes
            // brutes que la roue consomme. Sans source declaree, on garde le comportement d'avant.
            const sourceSel = button.dataset.studySource;
            let payload = chartJson;
            if (sourceSel) {
                const el = document.querySelector(sourceSel);
                if (!el) return;
                payload = localizeAstroJson(el.textContent, lang);
            }
            // `text/markdown` d'abord : un gabarit dont la sortie est du md refuse text/plain
            // (406 mesure). text/plain reste en repli pour un gabarit d'un autre type.
            const headers = { 'Content-Type': 'application/json', Accept: 'text/markdown, text/plain' };
            button.disabled = true;
            fetch(url, { method: 'POST', headers: csrfHeaders(url, headers), body: payload })
                .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
                .then((text) => {
                    titleEl.textContent = button.textContent.trim();
                    write(promptEl, text);
                    output.hidden = false;
                    output.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                })
                .catch((error) => {
                    titleEl.textContent = button.textContent.trim();
                    write(promptEl, `Erreur lors du rendu : ${error.message}`);
                    output.hidden = false;
                })
                .finally(() => {
                    button.disabled = false;
                });
        });
    });

    const copyButton = output.querySelector('[data-study-copy]');
    if (copyButton) {
        copyButton.addEventListener('click', () => {
            navigator.clipboard.writeText(read(promptEl)).then(() => {
                copyButton.classList.add('is-copied');
                setTimeout(() => copyButton.classList.remove('is-copied'), 1500);
            });
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-theme-studies]').forEach(attach);
});
